import { useMemo } from 'react';
import { humanize, shortDate } from '../../lib/formatLabels';
import { useStudentWellbeing } from '../../api/hooks';
import { isWellbeingRaw } from '../../api/types';
import { Badge, Spinner, ErrorPanel, EmptyState, PagedFooter, IconCircle } from '../../components/ui';
import { FaShieldAlt } from '../../components/ui/icons';
import Box from './components/Box';
import { usePagination } from '../../lib/usePagination';
import { dayMonth, dayMonthTime, possessive } from './lib/format';
import { withTime } from '../../lib/dates';

type Tone = 'success' | 'neutral' | 'warning' | 'danger';

// How a check-in answer reads at a glance; unknown answers stay neutral.
const STATE_TONE: Record<string, Tone> = {
  happy: 'success', neutral: 'neutral',
  sad: 'warning', anxious: 'warning', stressed: 'warning', tired: 'warning',
  overwhelmed: 'danger', burnt_out: 'danger',
};
// History dots follow the engagement dots (green / amber / red); the mood strip keeps slate for "ok".
const DOT: Record<Tone, string> = {
  success: 'bg-success', neutral: 'bg-success', warning: 'bg-idle', danger: 'bg-danger',
};
const STRIP: Record<Tone, string> = {
  success: 'bg-slate', neutral: 'bg-slate', warning: 'bg-idle', danger: 'bg-danger',
};
const DOT_BADGE = { ok: 'success', watch: 'warning', concern: 'danger' } as const;
const DOT_LABEL = { ok: 'OK', watch: 'Watch', concern: 'Concern' } as const;

export default function WellbeingTab({ id, gender }: { id: number; gender?: string | null }) {
  const q = useStudentWellbeing(id);
  const rawHistory = q.data && isWellbeingRaw(q.data) ? q.data.checkin_history : null;
  const entries = useMemo(() => (rawHistory ?? []).map((e, i) => {
    const state = String((e.payload as any)?.answer_slug ?? (e.payload as any)?.state ?? '');
    const note = String((e.payload as any)?.note ?? (e.payload as any)?.comment ?? '');
    return { id: i, at: e.created_at, state, note };
  }), [rawHistory]);
  const pg = usePagination(entries, 10);
  if (q.isLoading) return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  if (q.isError || !q.data) return <ErrorPanel />;
  const data = q.data;

  if (!isWellbeingRaw(data)) {
    return (
      <div className="flex items-start gap-3 rounded-[14px] bg-warning-bg p-4 text-sm text-warning-ink">
        <IconCircle icon={<FaShieldAlt />} tone="warning" size="sm" />
        <div>
          <p className="font-semibold">Wellbeing details are restricted.</p>
          <p className="mt-1">{data.message}</p>
          {data.has_open_case && <p className="mt-1">An open wellbeing case is being handled.</p>}
        </div>
      </div>
    );
  }

  // Last six check-ins, oldest first, for the mood strip.
  const strip = [...entries].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime()).slice(-6);
  const latest = data.latest_state ?? null;
  const latestTone: Tone = latest ? (STATE_TONE[latest] ?? 'neutral') : 'neutral';
  // "3 of the last 4 below OK": check-ins that read as watch or concern, newest four.
  const lastFour = [...entries].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()).slice(0, 4);
  const belowOk = lastFour.filter((e) => ['warning', 'danger'].includes(STATE_TONE[e.state] ?? 'neutral')).length;
  const dot = data.wellbeing_dot as keyof typeof DOT_BADGE;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-[14px] bg-mist px-3.5 py-3 text-[13px] text-ink">
        <FaShieldAlt className="mt-0.5 flex-none text-slate" aria-hidden="true" />
        <p>Visible to counsellors and school admins only. Teachers see the colour, not the answers. Treat with care.</p>
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Box title="Latest state" help="latest_state"
          actions={DOT_BADGE[dot] ? <Badge tone={DOT_BADGE[dot]} size="sm">{DOT_LABEL[dot]}</Badge> : undefined}>
          <p className="flex items-center gap-2.5 font-display text-3xl font-extrabold tracking-[-0.02em] text-ink">
            {latest && <span aria-hidden="true" className={`h-3 w-3 flex-none rounded-full ${DOT[latestTone]}`} />}
            {latest ? humanize(latest) : '—'}
          </p>
          <p className="mt-1.5 text-[13px] text-muted">
            Last check-in {data.last_checkin_at ? dayMonthTime(data.last_checkin_at) : '—'}
            {lastFour.length > 0 && ` · ${belowOk} of the last ${lastFour.length} below OK`}
          </p>
          {strip.length > 0 && (
            <>
              <div className="mt-3 flex gap-1.5" aria-label={`Last ${strip.length} check-ins, oldest first`}>
                {strip.map((e) => (
                  <span key={e.id} title={`${e.state ? humanize(e.state) : '—'}: ${withTime(e.at)}`}
                    className={`h-2.5 flex-1 rounded-[3px] ${STRIP[STATE_TONE[e.state] ?? 'neutral']}`} />
                ))}
              </div>
              <div className="mt-1.5 flex justify-between font-mono text-[11px] text-muted">
                <span>{shortDate(strip[0].at)}</span>
                {strip.length > 1 && <span>{shortDate(strip[strip.length - 1].at)}</span>}
              </div>
            </>
          )}
        </Box>
        <Box title="Career aspiration" help="What the student wrote in the app about the career they want." actions={<span className="text-xs text-muted">in {possessive(gender)} words</span>}>
          {data.career_aspiration_text ? (
            <blockquote className="whitespace-pre-wrap border-l-[3px] border-accent py-1 pl-3.5 text-[15px] italic text-ink">
              {data.career_aspiration_text}
            </blockquote>
          ) : <p className="text-sm text-muted">Nothing written yet.</p>}
        </Box>
      </div>

      <Box title="Check-in history" help="checkin_history" actions={entries.length > 0 ? <span className="text-xs text-muted">weekly, newest first</span> : undefined}>
        {entries.length === 0
          ? <EmptyState compact icon={<FaShieldAlt />} title="No check-ins yet" />
          : (
            <ul>
              {pg.pageItems.map((e) => (
                <li key={e.id} className="relative pb-3.5 pl-[22px] last:pb-0">
                  <span aria-hidden="true" className={`absolute left-1 top-1.5 h-2 w-2 rounded-full ${DOT[STATE_TONE[e.state] ?? 'neutral']}`} />
                  <span aria-hidden="true" className="absolute bottom-0 left-[7.5px] top-[18px] w-px bg-line-strong [li:last-child>&]:hidden" />
                  <span className="block font-mono text-[11px] font-medium text-muted">{dayMonth(e.at)}</span>
                  <span className="inline-flex items-center gap-[7px] text-sm font-medium text-ink">{e.state ? humanize(e.state) : '—'}</span>
                  {e.note && <span className="text-[13px] text-muted"> · {e.note}</span>}
                </li>
              ))}
            </ul>
          )}
        {entries.length > 0 && (
          <PagedFooter className="mt-3.5" label="Check-ins pagination" {...pg} onPage={pg.setPage} />
        )}
      </Box>
    </div>
  );
}
